// TurfAI Authentication Service

import { turfaiConfig } from './config';

interface LoginResponse {
  jwt: string;
  user: {
    id: number;
    username: string;
    email: string;
    provider: string;
    confirmed: boolean;
    blocked: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

// interface AuthTokens {
//   jwt: string;
//   apiKey: string;
//   expiresAt: number;
// }

export class TurfAIAuthService {
  private baseUrl: string;
  private tokenKey = 'turfai_auth_token';
  private tokenExpiryKey = 'turfai_token_expiry';
  private apiKeyKey = 'turfai_api_key';

  constructor() {
    this.baseUrl = turfaiConfig.dms.apiUrl;
  }

  // Login with username/email and password
  async login(identifier: string, password: string): Promise<LoginResponse> {
    try {
      const loginUrl = `${this.baseUrl}/auth/local`;
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier, // Strapi expects 'identifier' field
          password
        })
      });
      

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Login failed');
      }

      const data = await response.json() as LoginResponse;
      
      // Store JWT token with 24 hour expiry
      this.storeToken(data.jwt);
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Store tokens in localStorage with expiry
  private storeToken(jwt: string): void {
    const expiresAt = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
    localStorage.setItem(this.tokenKey, jwt);
    localStorage.setItem(this.tokenExpiryKey, expiresAt.toString());
    
    // Store API key from config (temporary for testing)
    const apiKey = turfaiConfig.auth.apiKey;
    if (apiKey) {
      localStorage.setItem(this.apiKeyKey, apiKey);
    }
  }

  // Get stored token if valid
  getToken(): string | null {
    const token = localStorage.getItem(this.tokenKey);
    const expiryStr = localStorage.getItem(this.tokenExpiryKey);
    
    if (!token || !expiryStr) {
      return null;
    }

    const expiry = parseInt(expiryStr);
    if (Date.now() > expiry) {
      this.clearToken();
      return null;
    }

    return token;
  }

  // Get stored API key
  getApiKey(): string | null {
    return localStorage.getItem(this.apiKeyKey) || turfaiConfig.auth.apiKey || null;
  }

  // Clear stored tokens
  clearToken(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.tokenExpiryKey);
    localStorage.removeItem(this.apiKeyKey);
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  // Get auth headers with JWT token
  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('No authentication token available. Please login first.');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Get dual auth headers (JWT + API key) for TurfAI public API
  getDualAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    const apiKey = this.getApiKey();
    
    if (!token) {
      throw new Error('No authentication token available. Please login first.');
    }

    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    } else {
    }

    return headers;
  }

  // Get upload headers (no content-type for FormData)
  getUploadHeaders(): Record<string, string> {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('No authentication token available. Please login first.');
    }

    return {
      'Authorization': `Bearer ${token}`,
    };
  }

  // Verify current token is valid
  async verifyToken(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/users/me`, {
        headers: this.getAuthHeaders()
      });
      
      return response.ok;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  }

  // Login with test credentials (for development)
  async loginWithTestCredentials(): Promise<LoginResponse> {
    return this.login('turfai@openturf.in', 'turf123@!');
  }

  // Refresh token if needed (Strapi doesn't have built-in refresh, so re-login)
  async refreshToken(identifier: string, password: string): Promise<string> {
    const loginData = await this.login(identifier, password);
    return loginData.jwt;
  }

  // Logout
  logout(): void {
    this.clearToken();
  }
}

// Export singleton instance
export const authService = new TurfAIAuthService();